import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from '@mui/material'
import ButtonCustom from '../../../components/atoms/button/ButtonCustom'
import {
	fetchPlatformFee,
	updatePlatformFee,
} from '../../../features/adminSlice'
import { toastHelper } from '../../../util/toastHelper'
import styles from './AdminConfigsPage.module.scss'

export function PricingConfigPanel() {
	const dispatch = useDispatch()
	const platformFee = useSelector((s) => s.admin.platformFee)
	const loading = useSelector((s) => s.admin.platformFeeLoading)
	const saving = useSelector((s) => s.admin.platformFeeSaving)
	const error = useSelector((s) => s.admin.platformFeeError)

	const [feeType, setFeeType] = useState('percentage')
	const [amount, setAmount] = useState('')

	useEffect(() => {
		dispatch(fetchPlatformFee())
	}, [dispatch])

	useEffect(() => {
		if (platformFee) {
			setFeeType(platformFee.feeType ?? 'percentage')
			setAmount(String(platformFee.amount ?? ''))
		}
	}, [platformFee])

	const handleSave = async () => {
		const parsed = Number(amount)
		if (Number.isNaN(parsed) || parsed < 0) {
			toastHelper('Amount must be a number greater than or equal to 0', 'error')
			return
		}

		const result = await dispatch(
			updatePlatformFee({
				appliesTo: 'vendor-payment',
				feeType,
				amount: parsed,
			})
		)

		if (updatePlatformFee.fulfilled.match(result)) {
			toastHelper('Platform fee updated', 'success')
		} else {
			toastHelper(
				result.payload || 'Failed to update platform fee',
				'error'
			)
		}
	}

	const amountLabel =
		feeType === 'percentage' ? 'Amount (%)' : 'Amount (cents)'

	return (
		<div className={styles.panelCard}>
			<Typography component="h2" className={styles.panelTitle}>
				Vendor platform fee
			</Typography>
			<p className={styles.panelBody}>
				Fee applied to vendor payments. Percentage is a percent of the
				charge; flat is a fixed amount in cents.
			</p>

			{error && (
				<p className={styles.errorText} role="alert">
					{error}
				</p>
			)}

			<div className={styles.formRow}>
				<FormControl size="small" className={styles.field}>
					<InputLabel id="fee-type-label">Fee type</InputLabel>
					<Select
						labelId="fee-type-label"
						label="Fee type"
						value={feeType}
						onChange={(e) => setFeeType(e.target.value)}
						disabled={loading || saving}
					>
						<MenuItem value="percentage">Percentage</MenuItem>
						<MenuItem value="flat">Flat (cents)</MenuItem>
					</Select>
				</FormControl>

				<TextField
					className={styles.field}
					size="small"
					type="number"
					label={amountLabel}
					value={amount}
					onChange={(e) => setAmount(e.target.value)}
					inputProps={{ min: 0, step: feeType === 'percentage' ? 0.01 : 1 }}
					disabled={loading || saving}
				/>

				<ButtonCustom
					variant="default"
					onClick={handleSave}
					disabled={loading || saving}
				>
					{saving ? 'Saving…' : 'Save'}
				</ButtonCustom>
			</div>

			{platformFee?.updatedAt && (
				<p className={styles.panelBody}>
					Last updated:{' '}
					{new Date(platformFee.updatedAt).toLocaleString()}
				</p>
			)}
		</div>
	)
}
